"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { fetchBatchMatchOdds, type BatchMatchOddsResult } from "@/app/(app)/actions/fetchBatchMatchOdds";
import type { FUSportsBettingOperatorOdds } from "fansunited-data-layer";
import { useLocale } from "@/lib/labels/LabelsProvider";

/**
 * Canonical batch odds type — same shape as the data-layer's FUSportsBatchMatchOdds
 * so downstream code (toMatchOddsMap, etc.) works unchanged.
 */
type BatchOddsMap = Map<string, FUSportsBettingOperatorOdds[]>;

/** Default polling interval in milliseconds (30 seconds) */
const DEFAULT_POLLING_INTERVAL_MS = 30000;
export interface UseServerBatchMatchOddsOptions {
  /** Array of match IDs to fetch odds for */
  matchIds: string[];
  /** Market types to include (default: ["1x2"]) */
  markets?: string[];
  /** Type of odds: PRE_EVENT, LIVE, or ALL (default: ALL) */
  oddType?: "PRE_EVENT" | "LIVE" | "ALL";
  /** Whether the matches are live — enables polling when true */
  isLive?: boolean;
  /** Polling interval in milliseconds (default: 30000) */
  pollingIntervalMs?: number;
  /** Filter odds by specific bookmaker/operator ID (applied client-side) */
  operatorId?: string;
  /** Locale for resolving tenant-specific odds config */
  locale?: string;
}

/**
 * Converts the serializable Record from the server action back to a Map,
 * matching the shape expected by toMatchOddsMap and other downstream code.
 */
function toMap(record: BatchMatchOddsResult): BatchOddsMap {
  const map: BatchOddsMap = new Map();
  for (const [key, value] of Object.entries(record)) {
    map.set(key, value);
  }
  return map;
}

/**
 * Drop-in replacement for `useBatchMatchOdds` from fansunited-data-layer/client.
 *
 * Instead of calling the Sportal365 API directly from the browser, this hook
 * fetches odds via a server action — keeping API credentials, odd_client,
 * and request details hidden from the client. Also enables future server-side
 * geoblocking via CF-IPCountry.
 *
 * Returns the same `{ oddsMap, loading, error, refetch }` shape.
 */
export function useServerBatchMatchOdds({
  matchIds,
  markets = ["1x2"],
  oddType = "ALL",
  isLive = false,
  pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
  operatorId,
  locale: localeProp
}: UseServerBatchMatchOddsOptions) {
  const contextLocale = useLocale();
  const locale = localeProp || contextLocale;
  const [oddsMap, setOddsMap] = useState<BatchOddsMap>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isInitialLoad = useRef(true);

  // Stable key so the effect only restarts when the set of IDs changes, not on every render
  const matchIdKey = useMemo(() => [...matchIds].sort().join(","), [matchIds]);
  const load = useCallback(async (isRefresh = false) => {
    if (matchIds.length === 0) {
      setOddsMap(new Map());
      setLoading(false);
      return;
    }
    if (!isRefresh) setLoading(true);
    setError(null);
    try {
      const result = await fetchBatchMatchOdds({
        matchIds,
        marketTypes: markets as Parameters<typeof fetchBatchMatchOdds>[0]["marketTypes"],
        oddType,
        locale: locale as Parameters<typeof fetchBatchMatchOdds>[0]["locale"]
      });
      let map = toMap(result);

      // Client-side operator filter (same behaviour as the data-layer hook)
      if (operatorId) {
        const filtered: BatchOddsMap = new Map();
        map.forEach((operators, id) => {
          const matched = operators.filter(op => op.operator.id === operatorId);
          if (matched.length > 0) filtered.set(id, matched);
        });
        map = filtered;
      }
      setOddsMap(map);
    } catch (err) {
      console.error("[useServerBatchMatchOdds] Error:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch batch odds"));
    } finally {
      setLoading(false);
      isInitialLoad.current = false;
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [matchIdKey, oddType, operatorId, locale]);

  // Initial load + re-fetch when dependencies change
  useEffect(() => {
    isInitialLoad.current = true;
    load(false);
  }, [load]);

  // Polling for live matches
  useEffect(() => {
    if (!isLive || matchIds.length === 0) return;
    const intervalId = setInterval(() => load(true), pollingIntervalMs);
    return () => clearInterval(intervalId);
  }, [isLive, matchIdKey, pollingIntervalMs, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => load(true), [load]);
  return {
    oddsMap,
    loading,
    error,
    refetch
  };
}